import React, { useState, useEffect } from "react";
import Header from "./Header";
import SubHeader from "./SubHeader";
import DataTable, { formatNum } from "./DataTable";
import TableLoader from "../TableLoader";
import Apiauth from "../../../API/Apiauth";
import CustomerSummaryCharts from "./graph/CustomerSummaryCharts";
import { toFormatPrice } from "../helpers/function";

function calculateAgeFromDob(customerDob) {
  if (
    !customerDob ||
    typeof customerDob.year !== "number" ||
    typeof customerDob.month !== "number" ||
    typeof customerDob.date !== "number"
  ) {
    return null;
  }

  const birthDate = new Date(
    customerDob.year,
    customerDob.month - 1,
    customerDob.date,
  );
  if (Number.isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const hasBirthdayPassed =
    monthDiff > 0 || (monthDiff === 0 && today.getDate() >= birthDate.getDate());
  if (!hasBirthdayPassed) age -= 1;

  return age >= 0 ? age : null;
}

function mapRows(apiData = []) {
  return apiData.map((row) => {
    const ageFromDob = calculateAgeFromDob(row.customer_dob);
    const resolvedAge = ageFromDob ?? row.customer_age;
    return {
      customerName: row.customer_name,
      group: row.customer_group,
      country: row.customer_country,
      gender: row.customer_gender,
      age: resolvedAge,
      invoices: row.invoices,
      qty: row.qty,
      salesAmount: row.sales,
    };
  });
}

function mapTotals(apiTotals = {}) {
  return {
    invoices: apiTotals.total_invoices,
    qty: apiTotals.total_qty,
    salesAmount: apiTotals.total_sales,
  };
}

function mapMetrics(summary = {}) {
  return [
    { label: "Total Customers", value: formatNum(summary.total_customer) },
    { label: "Total Invoices", value: formatNum(summary.total_invoices) },
    { label: "Total Qty Sold", value: formatNum(summary.total_qty) },
    { label: "Total Sales", value: toFormatPrice(Number(summary.total_sales || 0)) },
  ];
}

const DEFAULT_PAGE_SIZE = 10;
const CHART_MAX_LIMIT = 100000;

const DEFAULT_COLUMNS = [
  { key: "_index", label: "#", align: "center", width: 40 },
  { key: "customerName", label: "Customer Name", align: "left", width: 230 },
  { key: "group", label: "Group", align: "left", width: 150 },
  { key: "country", label: "Country", align: "left", width: 180 },
  { key: "gender", label: "Gender", align: "left", width: 120 },
  { key: "age", label: "Age", align: "left", width: 120 },
  { key: "invoices", label: "Invoices", align: "left", width: 100 },
  { key: "qty", label: "Qty", align: "left", width: 130 },
  {
    key: "salesAmount",
    label: "Sales Amount",
    align: "left",
    width: 130,
    isPriceColumn: true,
  },
];

const Customer = () => {
  const [rows, setRows] = useState([]);
  const [chartRows, setChartRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showCharts, setShowCharts] = useState(false);

  const fetchCustomerReport = ({
    page: p = 1,
    limit: l = pageSize,
    ...rest
  } = {}) => {
    setLoading(true);
    Apiauth({ page: p, limit: l, ...rest }, "Inventory/reports/customerSummary")
      .then((res) => {
        if (res?.code === 200) {
          setRows(mapRows(res.data));
          setTotals(mapTotals(res.totals));
          setMetrics(mapMetrics(res.summary));
          setFilter(res.filters);
          setTotalRecords(res?.pagination?.total ?? 0);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchChartReport = (filters = {}) => {
    setLoading(true);
    Apiauth(
      { page: 1, limit: CHART_MAX_LIMIT, ...filters },
      "Inventory/reports/customerSummary",
    )
      .then((res) => {
        if (res?.code === 200) {
          setChartRows(mapRows(res.data));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCustomerReport({ page: 1, limit: DEFAULT_PAGE_SIZE });
  }, []);

  useEffect(() => {
    if (showCharts) {
      fetchChartReport(appliedFilters);
    }
  }, [showCharts, appliedFilters]);

  function handleFetchData({ page: newPage, limit: newLimit }) {
    if (newLimit !== pageSize) setPageSize(newLimit);
    setPage(newPage);
    fetchCustomerReport({ ...appliedFilters, page: newPage, limit: newLimit });
  }

  function handleFilterApply(filterParams = {}) {
    setAppliedFilters(filterParams);
    setPage(1);
    fetchCustomerReport({ ...filterParams, page: 1, limit: pageSize });
  }

  return (
    <>
      <Header title="Customer Summary" metrics={metrics} />
      <SubHeader
        filters={filter}
        showCharts={showCharts}
        setShowCharts={setShowCharts}
        onFilterApply={handleFilterApply}
      />
      {loading ? (
        <TableLoader />
      ) : showCharts ? (
        <CustomerSummaryCharts rows={chartRows} />
      ) : (
        <DataTable
          columns={DEFAULT_COLUMNS}
          data={rows}
          totals={totals}
          showSearch={true}
          showDateRange={true}
          pageLimit={pageSize}
          totalRecords={totalRecords}
          currentPage={page}
          onPageChange={setPage}
          fetchData={handleFetchData}
        />
      )}
    </>
  );
};

export default Customer;
